import React, { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEgg,
  faDna,
  faEdit,
  faSkullCrossbones,
} from "@fortawesome/free-solid-svg-icons";
import "./GranjaMap.css";
import AddControlAvesForm from "../../Granja/Add/Forms/AddControlAvesForm/AddControlAvesForm";
import AddProduccionHuevosForm from "../../Granja/Add/Forms/AddProduccionHuevosForm/AddProduccionHuevosForm";
import AddControlAlimentoForm from "../../Granja/Add/Forms/AddControlAlimentoForm/AddControlAlimentoForm";
import AddAlimentoForm from "../../Granja/Add/Forms/AddAlimentoForm/AddAlimentoForm";
import AddAveForm from "../../Granja/Add/Forms/AddBatchForm/AddAveForm";
import ControlAlimentos from "./ControlAlimentos/ControlAlimento";
import ProduccionHuevos from "./ProduccionHuevos/ProduccionHuevos";
import ControlAves from "./ControlAves/ControlAves";
import GallinaAmarilla from "./GallinaAmarilla/GallinaAmarilla";
import { useShoppingContext } from "../../context/ShoppingContext";

const GranjaMap = () => {
  const {
    showAddControlAves,
    showAddControlAlimentos,
    showAddProduccion,
    showAddAlimento,
    showAddAves, // Estado para mostrar el formulario de AddAves
    fetchControlAves,
    fetchProduccionHuevos,
    fetchControlAlimentos,
    fetchAlimentos,
    aves,
    fetchMisGalpones,
    fetchMisAves,
    selectedAve,
    setSelectedAve,
    controlAlimentos,
    activeButton,
    setActiveButton,
  } = useShoppingContext();

  const { id: granjaId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchControlAves();
    fetchMisAves();
    fetchProduccionHuevos();
    fetchControlAlimentos();
    fetchAlimentos();
    fetchMisGalpones();
  }, [
    fetchControlAves,
    fetchAlimentos,
    fetchProduccionHuevos,
    fetchControlAlimentos,
    fetchMisGalpones,
    fetchMisAves,
  ]);

  const location = useLocation();
  const isGranjasRoute = location.pathname.startsWith("/granjas/");

  const handleButtonClick = (buttonKey, ave = null) => {
    setActiveButton((prev) => (prev === buttonKey ? "" : buttonKey));

    if (ave) {
      setSelectedAve(ave); // Establecer el ave seleccionada si existe
    }
  };

  const handleAveSelect = (ave) => {
    setSelectedAve(ave);
  };



  const granjaAves = aves.filter((ave) => ave.granjaId === parseInt(granjaId));

  // Establecer la primera ave como seleccionada por defecto si no hay una seleccionada
  useEffect(() => {
    if (!selectedAve && granjaAves.length > 0) {
      setSelectedAve(granjaAves[0]); // Seleccionar la primera ave de la lista
    }
  }, [granjaAves, selectedAve, setSelectedAve]);
  
  // Calcular la ingesta diaria por ave
  const calcularIngestaPorAve = () => {
    // Filtrar los registros de controlAlimentos donde ave_id es igual a selectedAve.id
    const registrosAlimento = controlAlimentos.filter(
      (control) => control.ave_id === selectedAve?.id
    );

    // Si no hay registros para el ave seleccionada, devolver "N/A"
    if (registrosAlimento.length === 0) {
      return "0.00";
    }

    // Ordenar los registros por fecha (asumiendo que controlAlimentos tiene un campo "fecha")
    const registroAlimentoReciente = registrosAlimento.sort(
      (a, b) => new Date(b.fecha) - new Date(a.fecha)
    )[0]; // Tomar el registro más reciente

    // Verificar que haya saldo actual para evitar divisiones por 0
    const saldoActual = selectedAve?.saldo || 1;

    // Calcular la ingesta en gramos por ave, de lo contrario, devolver "N/A"
    return registroAlimentoReciente
      ? (
          ((registroAlimentoReciente.ingesta_diaria -
            registroAlimentoReciente.sobra) /
            saldoActual) *
          1000
        ).toFixed(2) // Convertir a gramos por ave
      : "N/A";
  };

  // Calcular porcentaje de mortalidad
  const calcularPorcentajeMortalidad = () => {
    const mortalidad = selectedAve?.mortalidad || 0;
    const saldoInicial = selectedAve?.cantidad_inicial || 1;
    return ((mortalidad / saldoInicial) * 100).toFixed(2);
  };

  return (
    <div>
      <div className="aveSelectors">
        {granjaAves
          .sort((a, b) => a.edad_actual - b.edad_actual) // Ordena de menor a mayor edad
          .map((ave) => (
            <button
              key={ave.id}
              className={`aveName ${
                selectedAve && selectedAve.id === ave.id ? "active" : ""
              }`}
              onClick={() => handleAveSelect(ave)}
            >
              {ave.nombre}
            </button>
          ))}
      </div>
      <div className="bird-screen">
        <div className="granjaMap-header">
          <div>
            {/* Mostrar el icono de faDna y la genética del ave seleccionada */}
            {selectedAve && (
              <div className="genetics mt-2 gap-2">
                <FontAwesomeIcon icon={faDna} className="faDna mb-2" />
                <h4>{selectedAve.especie}</h4>{" "}
                <FontAwesomeIcon
                  onClick={() => navigate(`/edit-ave/${selectedAve.id}`)}
                  icon={faEdit}
                />
              </div>
            )}
            <p className="mb-0">{selectedAve?.saldo} Gallinas vivas</p>
            <p className="mb-0">Edad: {selectedAve?.edad_actual} semanas</p>
          </div>
        </div>

        {selectedAve && (
          <GallinaAmarilla
            ingestaPorAve={calcularIngestaPorAve()}
            porcentajeMortalidad={calcularPorcentajeMortalidad()}
            src="/images/icons/gallina-amarilla.png"
          />
        )}

        <div className="granja-map-footer">
          <button
            className={`btn-circle egg-button ${
              activeButton === "produccionHuevos" ? "active" : ""
            }`}
            onClick={() => handleButtonClick("produccionHuevos")}
          >
            <FontAwesomeIcon className="circle-icon" icon={faEgg} />
          </button>

          <button
            className={` btn-circle food-button ${
              activeButton === "controlAlimentos" ? "active" : ""
            }`}
            onClick={() => handleButtonClick("controlAlimentos")}
          >
            <img
              className="left-icon"
              height={40}
              src={"/images/icons/saco.png"}
              alt="saco"
            ></img>
          </button>

          <button
            className={`btn-circle reduce-button ${
              activeButton === "controlAves" ? "active" : ""
            }`}
            onClick={() => handleButtonClick("controlAves")}
          >
            <FontAwesomeIcon className="circle-icon" icon={faSkullCrossbones} />
          </button>
        </div>
      </div>

      {activeButton === "controlAves" && (
        <div className="table-container">
          <ControlAves />
          {!isGranjasRoute && showAddControlAves && <AddControlAvesForm />}
        </div>
      )}

      {activeButton === "produccionHuevos" && (
        <div className="table-container">
          <ProduccionHuevos />
          {!isGranjasRoute && showAddProduccion && <AddProduccionHuevosForm />}
        </div>
      )}

      {activeButton === "controlAlimentos" && (
        <div className="table-container">
          <ControlAlimentos />
          {!isGranjasRoute && showAddControlAlimentos && (
            <AddControlAlimentoForm />
          )}
        </div>
      )}

      {activeButton === "addAves" && showAddAves && (
        <div className="col-md-12">
          <AddAveForm /> {/* Mostramos el formulario AddAveForm */}
        </div>
      )}

      {!isGranjasRoute && showAddAlimento && <AddAlimentoForm />}
    </div>
  );
};

export default GranjaMap;
